/**
 * AUTO-GENERATED FROM config/layer_tiers.yaml BY generators/build_layer_tiers.py
 * DO NOT EDIT THIS FILE DIRECTLY — edit config/layer_tiers.yaml (or use tools/layer_tiers_editor.html)
 * and re-run `python3 generators/build_layer_tiers.py`.
 *
 * SkySkout Image / Tile Access Tiers (free < registered < pro < ultra):
 *   * Free - Unregistered users in the web ui or app
 *   * Registered - User that have made an account and are authenticated against firebase but are unpaid.
 *   * Pro - Users who have paid.
 *   * Ultra - for future use as a tier above pro.
 */

export type AccessTier = 'free' | 'registered' | 'pro' | 'ultra';

export const TIER_RANK: Record<AccessTier, number> = {
  free: 0,       // Free - Unregistered users in the web ui or app
  registered: 1, // Registered - User that have made an account and are authenticated against firebase but are unpaid.
  pro: 2,        // Pro - Users who have paid.
  ultra: 3,      // Ultra - for future use as a tier above pro.
};

export const TIER_LABELS: Record<AccessTier, string> = {
  free: 'Free',
  registered: 'Free Account',
  pro: 'Pro',
  ultra: 'Ultra',
};

export const SUBSCRIPTION_DENIAL_CONFIG = {
  unauthenticatedHttpStatus: 401,
  insufficientTierHttpStatus: 402,
  unauthenticatedCode: "AUTH_REQUIRED",
  insufficientTierCode: "SUBSCRIPTION_REQUIRED",
  headerDeniedReason: "X-SkySkout-Access-Denied",
  headerRequiredTier: "X-SkySkout-Required-Tier",
  headerUserTier: "X-SkySkout-User-Tier",
} as const;

export const LAYER_TIERS: Record<string, AccessTier> = {
  "alpenglow_forecast": "pro",
  "alpine_wildflowers": "pro",
  "aqi_index": "registered",
  "aqi_stations": "registered",
  "aurora_viewline": "pro",
  "avalanche_danger": "registered",
  "aviation_turbulence": "registered",
  "basemap_bortle_light_pollution": "registered",
  "basemap_elevation_dem": "registered",
  "basemap_hypsometric_relief": "registered",
  "basemap_osm_raster": "free",
  "basemap_recent_satellite_hls": "pro",
  "basemap_recent_satellite_modis": "pro",
  "basemap_skyskout_osm": "registered",
  "basemap_usgs_satellite": "free",
  "basemap_usgs_topo": "free",
  "cape": "pro",
  "climate_data": "registered",
  "cloud_base": "pro",
  "cloud_inversion": "pro",
  "clouds": "pro",
  "cpc_outlook": "registered",
  "darkskies_bortle": "pro",
  "desert_wildflowers": "pro",
  "dewpoint": "pro",
  "drought_usdm": "free",
  "dust_forecast": "pro",
  "fire_restrictions": "pro",
  "foliage": "pro",
  "forecast_point_tiles": "pro",
  "freezing_level": "pro",
  "golden_hour": "pro",
  "goldenwindow_nowcast": "pro",
  "hrtemp": "pro",
  "hrtemp_contours": "pro",
  "hrtemp_current": "registered",
  "jet_stream": "pro",
  "kp_index": "pro",
  "lenticular_clouds": "pro",
  "lightning": "pro",
  "lightning_live": "registered",
  "live_weather_qpe": "pro",
  "manifests_and_search": "free",
  "mountain_visibility": "pro",
  "mushrooms": "pro",
  "observed_point_tiles": "pro",
  "optics_dew_risk": "pro",
  "overlay_backcountry_features": "registered",
  "overlay_hiking_trails": "registered",
  "overlay_public_lands": "registered",
  "phenology_point_tiles": "pro",
  "precip_24h": "pro",
  "precip_accum": "pro",
  "precip_forecast": "pro",
  "precip_prob": "pro",
  "precip_radar": "registered",
  "recent_climate": "registered",
  "relative_humidity": "pro",
  "satellite_cloud_decks": "pro",
  "satellite_gmgsi": "registered",
  "satellite_imagery": "pro",
  "seeing": "pro",
  "smoke_forecast": "pro",
  "snow_24h": "pro",
  "snow_accum": "pro",
  "snow_depth": "pro",
  "snow_stations": "registered",
  "solar_lunar_tools": "registered",
  "sst": "pro",
  "streamflow_gauges": "pro",
  "sun_intensity_forecast": "pro",
  "thunderstorm": "pro",
  "tropical_cyclones": "free",
  "uv_index": "pro",
  "valley_fog": "pro",
  "valley_fog_point_tiles": "pro",
  "water_temp": "pro",
  "wild_berries": "pro",
  "wildfire_hotspots": "registered",
  "wildfires": "registered",
  "windchill": "pro",
  "winds_surface": "pro",
  "winds_surface_current": "pro",
  "wx_stations": "registered",
  "wx_stations_z4_shards": "free",
  "alpenglow": "pro",
  "aqi": "registered",
  "aurora": "pro",
  "avalanche": "registered",
  "climate": "registered",
  "climate_daily": "registered",
  "climate_monthly": "registered",
  "climate_weekly": "registered",
  "darkskies": "pro",
  "drought": "free",
  "dust": "pro",
  "foliage_canopy": "pro",
  "foliage_peak_date": "pro",
  "foliage_peak_date_treed": "pro",
  "foliage_soil_moisture_anomaly": "pro",
  "foliage_species": "pro",
  "foliage_species_aspen": "pro",
  "foliage_species_larch": "pro",
  "foliage_species_maple": "pro",
  "foliage_species_oak": "pro",
  "foliage_summer_temp_anomaly": "pro",
  "foliage_treed": "pro",
  "foliage_vibrancy": "pro",
  "foliage_vibrancy_treed": "pro",
  "freezing_level_contours": "pro",
  "gmgsi": "registered",
  "goldenhour": "pro",
  "lenticular": "pro",
  "lightning_24h": "pro",
  "live_weather": "registered",
  "mrms": "registered",
  "mushrooms_current": "pro",
  "mushrooms_outlook": "pro",
  "nowcast": "pro",
  "precip": "pro",
  "precip_pop": "pro",
  "precip_rain": "pro",
  "precip_snow": "pro",
  "radar": "registered",
  "rain_accum": "pro",
  "satellite": "pro",
  "smoke": "pro",
  "streamflow": "pro",
  "sun_intensity": "pro",
  "sun_intensity_actual": "pro",
  "sunrise": "pro",
  "sunrise_sunset": "pro",
  "surface_winds": "pro",
  "tropical": "free",
  "turbulence": "registered",
  "winds": "pro"
};

export const BASEMAP_THEME_TIERS: Record<string, AccessTier> = {
  "dark": "registered",
  "light": "registered",
  "neutral": "registered",
  "openstreetmap": "free",
  "osm": "free",
  "recent-satellite": "pro",
  "road": "registered",
  "terrain": "registered",
  "terrain-mapterhorn": "registered",
  "usgs-hillshade": "free",
  "usgs-naip": "free",
  "usgs-topo": "free"
};

export function getRequiredTierForLayer(layerId: string | null | undefined): AccessTier {
  if (!layerId) return 'free';
  const key = layerId.trim();
  return LAYER_TIERS[key] || LAYER_TIERS[key.toLowerCase()] || 'pro';
}

export function getRequiredTierForBasemapTheme(theme: string | null | undefined, mountainMode?: string): AccessTier {
  if (!theme) return BASEMAP_THEME_TIERS['road'] || 'registered';
  if (theme === 'recent-satellite' && mountainMode && mountainMode !== 'off') {
    return LAYER_TIERS['basemap_recent_satellite_hls'] || 'pro';
  }
  return BASEMAP_THEME_TIERS[theme] || 'registered';
}

export function hasTierAccess(userTier: AccessTier, requiredTier: AccessTier): boolean {
  return TIER_RANK[userTier] >= TIER_RANK[requiredTier];
}
